// Pure utilities for parsing and serializing SRT and VTT subtitle files,
// and computing a plain-text transcript from cues.
//
// The serializer round-trips the verbatim `identifier` and `timing` lines from
// the original file. Only the cue `text` is intended to be modified between
// parse and serialize, which keeps cue numbering and timestamps untouched.

export interface Cue {
    /** Raw identifier line (SRT sequence number or VTT cue label). Empty if absent. */
    identifier: string;
    /** Raw timing line, including any VTT cue settings. e.g. "00:00:00,000 --> 00:00:03,000" */
    timing: string;
    /** Cue text, possibly multi-line (joined with "\n"). */
    text: string;
}

export interface ParsedVtt {
    /** "WEBVTT" + any metadata lines up to the first blank line, preserved verbatim. */
    header: string;
    /** Cue blocks and raw NOTE/STYLE/REGION blocks in original order. */
    blocks: Array<{ type: "cue"; cue: Cue } | { type: "raw"; content: string }>;
}

const normalize = (s: string): string => s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

const splitBlocks = (s: string): string[] =>
    s.split(/\n\n+/).map((b) => b.replace(/^\n+|\n+$/g, "")).filter((b) => b.length > 0);

const isCueBlock = (block: string): boolean => block.includes("-->");

const parseCueBlock = (block: string): Cue => {
    const lines = block.split("\n");
    let identifier = "";
    let timingIdx = 0;
    if (lines.length > 0 && !lines[0].includes("-->")) {
        identifier = lines[0];
        timingIdx = 1;
    }
    const timing = lines[timingIdx] || "";
    const text = lines.slice(timingIdx + 1).join("\n");
    return { identifier, timing, text };
};

const cueToString = (c: Cue): string => {
    const head = c.identifier ? `${c.identifier}\n${c.timing}` : c.timing;
    return c.text ? `${head}\n${c.text}` : head;
};

export const parseSrt = (content: string): Cue[] => {
    return splitBlocks(normalize(content)).map(parseCueBlock);
};

export const serializeSrt = (cues: Cue[]): string => {
    return cues.map(cueToString).join("\n\n") + "\n";
};

export const parseVtt = (content: string): ParsedVtt => {
    const text = normalize(content);
    const firstBlank = text.match(/\n\n+/);

    let header = "";
    let body = "";
    if (!firstBlank) {
        header = text.trim();
        body = "";
    } else {
        const idx = firstBlank.index!;
        header = text.slice(0, idx);
        body = text.slice(idx + firstBlank[0].length);
    }

    const blocks: ParsedVtt["blocks"] = splitBlocks(body).map((b) =>
        isCueBlock(b)
            ? { type: "cue" as const, cue: parseCueBlock(b) }
            : { type: "raw" as const, content: b }
    );

    return { header, blocks };
};

export const serializeVtt = (parsed: ParsedVtt): string => {
    const blocksOut = parsed.blocks.map((b) => (b.type === "cue" ? cueToString(b.cue) : b.content));
    const parts = parsed.header ? [parsed.header, ...blocksOut] : blocksOut;
    return parts.join("\n\n") + "\n";
};

/**
 * Extract start/end timestamps from a timing line for display purposes.
 * Returns null if the line cannot be parsed; callers should fall back to
 * showing the raw timing line.
 */
export const extractTimestamps = (timing: string): { start: string; end: string } | null => {
    const m = timing.match(/^(\S+)\s*-->\s*(\S+)/);
    if (!m) return null;
    return { start: m[1], end: m[2] };
};

/** Strip milliseconds for compact display: "00:00:03,500" → "00:00:03". */
export const stripMs = (ts: string): string => ts.replace(/[.,]\d+$/, "");

/** Parse "HH:MM:SS[,.mmm]" or "MM:SS" into seconds. NaN on parse failure. */
export const timestampToSeconds = (ts: string): number => {
    if (!ts) return NaN;
    const normalized = ts.replace(",", ".").trim();
    const parts = normalized.split(":");
    if (parts.length < 2 || parts.length > 3) return NaN;
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => Number.isNaN(n))) return NaN;
    return parts.length === 3
        ? nums[0] * 3600 + nums[1] * 60 + nums[2]
        : nums[0] * 60 + nums[1];
};

/** Compact display: under one hour → "M:SS", else "H:MM:SS". */
export const formatShortTimestamp = (ts: string): string => {
    const total = timestampToSeconds(ts);
    if (Number.isNaN(total)) return ts;
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = Math.floor(total % 60);
    const ss = s.toString().padStart(2, "0");
    if (h > 0) {
        const mm = m.toString().padStart(2, "0");
        return `${h}:${mm}:${ss}`;
    }
    return `${m}:${ss}`;
};

/**
 * Concatenation rule the pipeline uses to derive the DB transcript.
 * Joins cue texts with "\n". Verify by inspection on first integration; if
 * the pipeline uses " " instead, change the separator here.
 */
export const transcriptFromCues = (cues: Cue[]): string => {
    return cues.map((c) => c.text).join("\n");
};

/** Replace cue text positionally; throws if cue counts differ. */
export const replaceCueTextByIndex = (target: Cue[], source: Cue[]): Cue[] => {
    if (target.length !== source.length) {
        throw new Error(
            `Cue count mismatch: target has ${target.length}, source has ${source.length}`
        );
    }
    return target.map((c, i) => ({ ...c, text: source[i].text }));
};
